import { alpha, Button, Card, CardContent, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { personalDataStore } from 'stores/personalDataStore';
import { PATHS } from 'routes/paths';
import { useAuthForm } from 'hooks/useAuthForm';
import {
  LoginForm,
  RegisterForm,
  SetPasswordForm,
  ResetSendForm,
  ResetVerifyForm
} from 'components/Login';
import FeedbackDialog from '../components/FeedbackDialog';
import PrivacyNoticeDialog from '../components/PrivacyNoticeDialog';

export default function LoginPage() {
  // Снимок авторизации на момент монтирования. Если пользователь уже вошёл в
  // систему и попал на /login (по ошибке или намеренно) — возвращаем его назад
  // по истории браузера, а если предыдущей страницы нет — на главную. Вход
  // через форму это не затрагивает: там редиректом управляет loginAndRedirect.
  const authedOnMount = useMemo(() => personalDataStore.isAuthenticated, []);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authedOnMount) return;
    const hasHistory = (window.history.state?.idx ?? 0) > 0;
    if (hasHistory) {
      navigate(-1);
    } else {
      navigate(PATHS.main, { replace: true });
    }
  }, [authedOnMount, navigate]);

  if (authedOnMount) return null;

  return <LoginPageContent />;
}

function LoginPageContent() {
  // При наличии query-параметра ?login форма открывается сразу на вкладке «Вход»,
  // иначе — по умолчанию на регистрации.
  const [searchParams] = useSearchParams();
  const auth = useAuthForm(searchParams.has('login') ? 'login' : 'register');
  const { mode, ui, isResetMode } = auth;
  const patternUrl = '/static/brand/patternVert.svg';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack
      sx={{
        height: '100%',
        position: 'relative',
        backgroundImage: `url(${patternUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: isMobile ? '150%' : 'cover',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha(theme.palette.background.default, 0.92),
          zIndex: 0
        }
      }}
      justifyContent="center"
      alignItems="center"
      spacing={2}
    >
      <Card
        sx={{
          width: { xs: '100%', sm: '50%' },
          maxWidth: 600,
          minHeight: 500,
          height: { xs: '65vh', sm: 480 },
          boxShadow: 6,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {!isResetMode && (
          <Tabs value={auth.tabValue} onChange={auth.handleTabChange} variant="fullWidth">
            <Tab label="Вход" />
            <Tab label="Регистрация" />
          </Tabs>
        )}

        <CardContent
          sx={{
            p: 6,
            '&:last-child': { pb: 6 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {(mode === 'reset-send' || mode === 'reset-verify') && (
            <Button
              startIcon={<Icon icon="mdi:arrow-left" />}
              onClick={auth.backToLogin}
              sx={{ mb: 1, textTransform: 'none', ml: -1 }}
            >
              Вернуться назад
            </Button>
          )}

          {mode === 'set-password' && (
            <Button
              startIcon={<Icon icon="mdi:arrow-left" />}
              onClick={auth.backToRegister}
              sx={{ mb: 1, textTransform: 'none', ml: -1 }}
            >
              Назад
            </Button>
          )}

          {mode === 'login' && <LoginForm auth={auth} />}
          {mode === 'register' && <RegisterForm auth={auth} />}
          {mode === 'set-password' && <SetPasswordForm auth={auth} />}
          {mode === 'reset-send' && <ResetSendForm auth={auth} />}
          {mode === 'reset-verify' && <ResetVerifyForm auth={auth} />}
        </CardContent>
      </Card>

      <Card sx={{ maxWidth: 300 }}>
        <CardContent
          sx={{ p: 3, display: 'flex', justifyContent: 'center', '&:last-child': { pb: 3 } }}
        >
          <Button
            onClick={() => auth.updateUi({ feedbackOpen: true })}
            sx={{ textDecoration: 'none', textTransform: 'none', p: 0 }}
          >
            <Typography variant="body2" fontWeight={600} color="text.primary">
              Написать поддержке
            </Typography>
          </Button>
        </CardContent>
      </Card>

      <FeedbackDialog
        open={ui.feedbackOpen}
        onClose={() => auth.updateUi({ feedbackOpen: false })}
      />
      <PrivacyNoticeDialog
        open={ui.privacyDialogOpen}
        onClose={() => auth.updateUi({ privacyDialogOpen: false })}
        onAccept={() => auth.updateUi({ consentChecked: true, privacyDialogOpen: false })}
      />
    </Stack>
  );
}
